// The checklist itself: percent, progress bar, one row per step, footer CTA.
//
// Extracted so it can live in two shells:
//   - OnboardingChecklistSheet — the dismissable global modal (welcome nudge,
//     header ring, avatar dropdown, Settings card).
//   - Screens/Books/CsocProfileGate — a blocking overlay inside School of
//     Chess, which must NOT be a screen-covering modal because the left menu
//     has to stay reachable.
//
// Pass `onDismiss` for the dismissable shell; omit it for the blocking one and
// the "Skip for now" link disappears.

import React, {useState} from 'react';
import {View, TouchableOpacity} from 'react-native';
import Icon from 'react-native-vector-icons/FontAwesome5';
import CCText from '@/Atoms/CCText';
import CCButton from '@/Atoms/CCButton';
import CCTextInput from '@/Atoms/CCTextInput';
import CCColors from '@/Utils/CCColors';
import useOnboardingProgressStore, {
  useOnboardingProgress,
} from '@/Store/onboardingProgressStore';
import useLoggedInUserStore from '@/Store/loggedInUserstore';
import useUpdateProfileApi from '@/Hooks/ProfileApi/profileUpdateApi';
import {getUserDetails, setLocalStorage} from '@/Utils/CommonUtils';
import {StepKey} from '@/Utils/onboardingSteps';

const BROWN = CCColors.Primary.Brown;
const CREAM = CCColors.Primary.lightBrown;
const GREY = CCColors.TextColor.GreyText;

// Short helper line per step, shown under the label.
const STEP_SUBS: Record<string, string> = {
  profile: 'Your name & where you play from',
  username: 'How other players see you',
  connect_accounts: 'Pull your games & rating in',
  player_linked: 'Save your profile once more to finish linking your account',
};

// Mirrors the backend's save_profile username check (chess/chatbot) — keep
// the shape in sync if that rule changes.
const isValidUsernameFormat = (value: string) =>
  value.length >= 4 && /^[A-Za-z0-9_-]+$/.test(value);

interface UsernameEditorProps {
  currentUsername: string;
  /** Called with the newly-saved username right after a successful save. */
  onSaved: (username: string) => void;
}

// Inline keep-or-change editor for the `username` step. Replaces the old
// standalone dashboard prompt modal -- this IS the step's action now, so the
// row above it doesn't navigate anywhere (route: '' in onboardingSteps.ts).
function UsernameEditor({currentUsername, onSaved}: UsernameEditorProps) {
  const [value, setValue] = useState(currentUsername);
  const [serverError, setServerError] = useState('');

  const trimmed = value.trim();
  const isFormatValid = isValidUsernameFormat(trimmed);
  const isDifferent = trimmed.toLowerCase() !== currentUsername.toLowerCase();
  const canSave = isFormatValid && isDifferent;
  const formatHint =
    trimmed && !isFormatValid ? 'Use 4+ letters, numbers, _ or -' : '';
  const displayError = serverError || formatHint;

  // Same merge Settings/index.tsx (and the removed modal) did after a save:
  // patch the persisted user_details blob, then the logged-in user store, so
  // both agree with the server without waiting for the next summary fetch.
  const handleSaveSuccess = async (data: any) => {
    const savedUsername = data?.data?.[0]?.fields?.username ?? trimmed;
    const stored = await getUserDetails();
    const parsed = stored ? JSON.parse(stored) : {};
    parsed.username = savedUsername;
    setLocalStorage('user_details', JSON.stringify(parsed), false, () => {});
    const prevUserData = useLoggedInUserStore.getState().userData;
    useLoggedInUserStore
      .getState()
      .setUserData({...prevUserData, username: savedUsername});
    // Also patch the onboarding summary snapshot: the sheet unmounts on
    // close, so without this a reopen would re-derive the row subtitle from
    // the stale snapshot and show "You're @<old-name>" until the next
    // summary fetch.
    useOnboardingProgressStore.setState(s => ({
      summarySnapshot: {
        ...s.summarySnapshot,
        user: {...(s.summarySnapshot as any)?.user, username: savedUsername},
      },
    }));
    useOnboardingProgressStore.getState().markStepDone('username');
    onSaved(savedUsername);
  };

  const {mutate: updateProfile, isLoading: profileLoader} = useUpdateProfileApi(
    handleSaveSuccess,
    error => {
      setServerError(
        error?.error || 'Could not update username. Please try again.',
      );
    },
  );

  const handleSave = () => {
    if (!canSave) {
      return;
    }
    updateProfile({username: trimmed});
  };

  const handleKeep = () => {
    useOnboardingProgressStore.getState().markStepDone('username');
  };

  return (
    <View
      style={{
        backgroundColor: CCColors.White,
        borderRadius: 12,
        borderWidth: 1,
        borderColor: CCColors.BorderLight,
        padding: 10,
        marginTop: -4,
        marginBottom: 8,
      }}>
      <CCTextInput
        label="Username"
        value={value}
        onChangeText={text => {
          setValue(text);
          setServerError('');
        }}
        error={{flag: !!displayError, msg: displayError}}
        fontSize={13}
        style={{height: 40, paddingHorizontal: 10}}
      />
      <View style={{flexDirection: 'row', marginTop: 8}}>
        <CCButton
          type="outline"
          disabled={profileLoader}
          style={{flex: 1, marginRight: 8}}
          contentContainerStyle={{paddingVertical: 6, paddingHorizontal: 10}}
          onPress={handleKeep}>
          <CCText
            fontSize={12.5}
            fontWeight="medium"
            numberOfLines={1}
            showEllipses>
            Keep @{currentUsername}
          </CCText>
        </CCButton>
        <CCButton
          style={{flex: 1}}
          contentContainerStyle={{paddingVertical: 6, paddingHorizontal: 10}}
          disabled={!canSave}
          loading={profileLoader}
          onPress={handleSave}>
          <CCText fontSize={12.5} fontWeight="medium">
            Save
          </CCText>
        </CCButton>
      </View>
    </View>
  );
}

interface Props {
  /** Called with a step's route when the user chooses to go and do it. */
  onNavigate: (route: string) => void;
  /** Omit to make the checklist non-dismissable (no "Skip for now"). */
  onDismiss?: () => void;
  /** Replaces the skip link when there isn't one — explains why it's stuck. */
  footerNote?: string;
  /**
   * Show (and count) only these steps. Used by the School of Chess gate, which
   * requires the profile and username but not a linked Lichess/Chess.com
   * account. Omit for the full checklist.
   */
  stepKeys?: StepKey[];
}

export default function ChecklistBody({
  onNavigate,
  onDismiss,
  footerNote,
  stepKeys,
}: Props) {
  const progress = useOnboardingProgress();
  // Signup default is the account phone (see onboardingSteps' summaryDone),
  // so the username step shows the CURRENT value either way -- done or not --
  // rather than a generic blurb, making the choice (or the lack of one)
  // visible at a glance.
  const username = useOnboardingProgressStore(s =>
    String(s.summarySnapshot?.user?.username ?? '').trim(),
  );
  // A successful save flips the username step's `done` flag immediately
  // (markStepDone), but `summarySnapshot` only refreshes on the next full
  // summary fetch -- without this override the row would flip to "done"
  // against the OLD name for a beat. Local just-saved state wins until then.
  const [justSavedUsername, setJustSavedUsername] = useState<string | null>(
    null,
  );
  const displayUsername = justSavedUsername ?? username;

  // Percent has to be recomputed over the subset, not taken from the store —
  // otherwise a gate on 2 steps would read the 3-step number and could sit at
  // 67% while showing "2 of 2 done".
  const steps = stepKeys
    ? progress.steps.filter(s => stepKeys.includes(s.key))
    : progress.steps;
  const completed = steps.filter(s => s.done).length;
  const total = steps.length;
  const percent = total === 0 ? 100 : Math.round((completed / total) * 100);

  const allDone = percent >= 100;
  const nextStep = steps.find(s => !s.done && s.route);

  return (
    <View style={{paddingHorizontal: 4, paddingBottom: 8, minWidth: 288}}>
      <View
        style={{flexDirection: 'row', alignItems: 'baseline', marginTop: 2}}>
        <CCText fontSize={30} fontWeight="semibold" style={{color: BROWN}}>
          {percent}%
        </CCText>
        <CCText fontSize={13} style={{color: GREY, marginLeft: 8}}>
          {allDone ? 'profile complete' : `${completed} of ${total} done`}
        </CCText>
      </View>
      <View
        style={{
          height: 8,
          borderRadius: 100,
          backgroundColor: CREAM,
          overflow: 'hidden',
          marginTop: 8,
          marginBottom: 16,
        }}>
        <View
          style={{
            width: `${Math.max(4, percent)}%`,
            height: '100%',
            borderRadius: 100,
            backgroundColor: allDone
              ? CCColors.Green
              : CCColors.Primary.Default,
          }}
        />
      </View>

      {steps.map(s => {
        // The username step's secondary text shows the actual current value
        // (done or not) instead of the generic STEP_SUBS copy -- see the
        // `displayUsername` const above.
        const subtitle =
          s.key === 'username'
            ? s.done
              ? `You're @${displayUsername}`
              : displayUsername
              ? `Currently @${displayUsername} — pick how other players see you`
              : 'Pick how other players see you'
            : !s.done
            ? STEP_SUBS[s.key]
            : null;
        return (
          <React.Fragment key={s.key}>
            <TouchableOpacity
              disabled={s.done || !s.route}
              activeOpacity={0.7}
              onPress={() => onNavigate(s.route)}
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                alignSelf: 'stretch',
                backgroundColor: s.done ? CCColors.White : CREAM,
                borderRadius: 12,
                paddingVertical: 12,
                paddingHorizontal: 12,
                marginBottom: 8,
                borderWidth: 1,
                borderColor: s.done ? CCColors.BorderLight : 'transparent',
              }}>
              <View
                style={{
                  width: 34,
                  height: 34,
                  borderRadius: 25,
                  backgroundColor: s.done
                    ? CCColors.Green
                    : CCColors.LightYellow,
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginRight: 12,
                }}>
                <Icon
                  name={s.done ? 'check' : s.icon}
                  size={14}
                  color={s.done ? CCColors.White : BROWN}
                  solid={s.done}
                />
              </View>
              <View style={{flex: 1}}>
                <CCText
                  fontSize={14}
                  fontWeight="semibold"
                  style={{
                    color: s.done ? GREY : BROWN,
                    textDecorationLine: s.done ? 'line-through' : 'none',
                  }}>
                  {s.label}
                </CCText>
                {subtitle ? (
                  <CCText fontSize={11.5} style={{color: GREY, marginTop: 1}}>
                    {subtitle}
                  </CCText>
                ) : null}
              </View>
              {!s.done && s.route ? (
                <View
                  style={{
                    width: 26,
                    height: 26,
                    borderRadius: 100,
                    backgroundColor: CCColors.Primary.Default,
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}>
                  <Icon name="chevron-right" size={11} color={CCColors.White} />
                </View>
              ) : null}
            </TouchableOpacity>
            {/* The username step's action lives here, not on a route -- see
                the `route: ''` comment in onboardingSteps.ts. Always visible
                while pending; disappears once done. */}
            {s.key === 'username' && !s.done ? (
              <UsernameEditor
                currentUsername={displayUsername}
                onSaved={setJustSavedUsername}
              />
            ) : null}
          </React.Fragment>
        );
      })}

      <View style={{marginTop: 8}}>
        {allDone ? (
          <CCButton onPress={() => onDismiss?.()}>
            <CCText fontSize={14} fontWeight="semibold">
              Done
            </CCText>
          </CCButton>
        ) : nextStep ? (
          <>
            <CCButton animated onPress={() => onNavigate(nextStep.route)}>
              <CCText fontSize={14} fontWeight="semibold">
                Continue — {nextStep.label.toLowerCase()}
              </CCText>
            </CCButton>
            {onDismiss ? (
              <TouchableOpacity
                onPress={onDismiss}
                style={{
                  marginTop: 10,
                  alignSelf: 'center',
                  paddingVertical: 4,
                }}>
                <CCText fontSize={13} style={{color: GREY}}>
                  Skip for now
                </CCText>
              </TouchableOpacity>
            ) : footerNote ? (
              <CCText
                fontSize={12}
                style={{color: GREY, marginTop: 10, textAlign: 'center'}}>
                {footerNote}
              </CCText>
            ) : null}
          </>
        ) : onDismiss ? (
          // Nothing left with a route to jump to -- the only pending step
          // (username) has its action inline above, not here -- but the
          // sheet itself can still be dismissed.
          <CCButton onPress={() => onDismiss?.()}>
            <CCText fontSize={14} fontWeight="semibold">
              Done
            </CCText>
          </CCButton>
        ) : footerNote ? (
          // Non-dismissable shell (CsocProfileGate) with nothing routable
          // left: no CTA to show, so surface the same footerNote it would've
          // shown alongside a Continue button, instead of a dead-end button.
          <CCText
            fontSize={12}
            style={{color: GREY, marginTop: 10, textAlign: 'center'}}>
            {footerNote}
          </CCText>
        ) : null}
      </View>
    </View>
  );
}
